import { redirect } from '@tanstack/react-router';
import type { LearnArticle } from '~/content/learn';
import type { LocalizedLocale } from '~/lib/localized-routes';
import { getArticleByFullPath } from '~/lib/payload';
import {
  absoluteAssetUrl,
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  jsonLdScript,
  ORGANIZATION_ID,
  webPageJsonLd,
} from '~/lib/seo';
import { localizedAlternateLinks, localizedPath } from '~/lib/localized-routes';
import readingStylesHref from '~/styles/reading.css?url';

export function buildArticleFullPath(urlPrefix: string, slug: string): string {
  return `${urlPrefix}/${slug}`;
}

export function learnArticleHeadMeta(article: LearnArticle | undefined, locale?: LocalizedLocale) {
  const title =
    article?.metaTitle ?? article?.title ?? 'Learn with nsave – Step by Step Guides / Articles';
  const description =
    article?.metaDescription ?? article?.excerpt ?? 'Guides and support for using nsave.';
  const canonicalPath = article?.fullPath ? `/${article.fullPath}` : undefined;
  const canonical = canonicalPath
    ? absoluteUrl(locale ? localizedPath(canonicalPath, locale) : canonicalPath)
    : undefined;
  const ogImage = absoluteAssetUrl(article?.ogImageUrl ?? DEFAULT_OG_IMAGE);
  const isDefaultOgImage = !article?.ogImageUrl;
  const ogImageAlt =
    article?.ogImageAlt ??
    (isDefaultOgImage ? 'nsave — A global account for the rest of us' : undefined);
  const ogImageWidth = article?.ogImageWidth ?? (isDefaultOgImage ? 2434 : undefined);
  const ogImageHeight = article?.ogImageHeight ?? (isDefaultOgImage ? 1278 : undefined);
  const ogImageType = article?.ogImageMimeType ?? (isDefaultOgImage ? 'image/jpeg' : undefined);

  const meta: Array<
    { title: string } | { name: string; content: string } | { property: string; content: string }
  > = [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    ...(canonical ? [{ property: 'og:url' as const, content: canonical }] : []),
    { property: 'og:site_name', content: 'nsave' },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:secure_url', content: ogImage },
    ...(ogImageType ? [{ property: 'og:image:type' as const, content: ogImageType }] : []),
    ...(ogImageWidth
      ? [{ property: 'og:image:width' as const, content: String(ogImageWidth) }]
      : []),
    ...(ogImageHeight
      ? [{ property: 'og:image:height' as const, content: String(ogImageHeight) }]
      : []),
    ...(ogImageAlt ? [{ property: 'og:image:alt' as const, content: ogImageAlt }] : []),
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: ogImage },
    ...(ogImageAlt ? [{ name: 'twitter:image:alt' as const, content: ogImageAlt }] : []),
  ];

  const links = [
    ...(canonical ? [{ rel: 'canonical', href: canonical }] : []),
    ...(canonicalPath ? localizedAlternateLinks(canonicalPath, absoluteUrl) : []),
    { rel: 'stylesheet', href: readingStylesHref },
  ];

  const parsedDate = article?.date ? new Date(article.date) : null;
  const datePublished =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toISOString().slice(0, 10)
      : undefined;
  const scripts =
    article && canonical
      ? [
          webPageJsonLd({ title, description, canonical, image: ogImage }),
          jsonLdScript({
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': `${canonical}#article`,
            mainEntityOfPage: { '@id': `${canonical}#webpage` },
            headline: article.title,
            description,
            image: [ogImage],
            ...(datePublished ? { datePublished } : {}),
            author: article.author.name.toLowerCase().includes('nsave')
              ? { '@id': ORGANIZATION_ID }
              : { '@type': 'Person', name: article.author.name },
            publisher: { '@id': ORGANIZATION_ID },
            inLanguage: 'en',
          }),
        ]
      : [];

  return { meta, links, scripts };
}

export async function loadLearnArticle(urlPrefix: string, slug: string) {
  const fullPath = buildArticleFullPath(urlPrefix, slug);
  const article = await getArticleByFullPath({ data: fullPath });
  if (!article) throw redirect({ to: '/help' });
  return { article };
}
